import type { Item, ItemDef } from "./types";

/**
 * How the bag is laid out: the piles the server sends, a square each, put in
 * order.
 *
 * Both halves of that used to be the server's and arrived finished. A bag
 * was a list of squares, sorted before every send, and the client drew the
 * list as it stood. Neither half was ever about the world: which square a
 * rock sits in and which square comes first are questions about a picture,
 * and the server was answering them ten times a second for every player
 * online to hand back a layout that was nearly always the one it sent last
 * (see the note on Inventory in internal/game/inventory.go). What it sends
 * now is what it knows — a pile of a kind, and how much of it there is —
 * and the picture is worked out here.
 *
 * A pile is one square, however much is in it. From 2026-09-23 a square
 * held only so many of a kind and this cut a big pile into several; that
 * went on 2026-10-04 ("Remove item stacking and reintroduce encumbrance.
 * Replace the stack count helper button with invert"), and five hundred
 * rocks are one square with 500 in its corner again.
 *
 * The order is the one chosen on 2026-09-24 ("always sort the inventory"),
 * less one thing: what is in use no longer goes first (2026-09-27: "don't
 * reorder equipped items to the start of the inventory"). It sits among
 * its own kind like anything else, marked as in use where it lies, so
 * picking up a sword does not shuffle the bag. By kind: potions and orbs,
 * rings, weapons, shields, armor, cloaks, helmets, then everything that is
 * none of those, then food, and gold last (2026-10-10: "when sorting by kind, sort
 * potions and orbs first, rings second, food and gold always last"). Within
 * a kind, the most valuable first — what one of it is worth, its price —
 * then by name, then by whose it is (the same day: "within a band, sort by
 * single item value descending").
 *
 * The weapons go two-handed melee, then ranged, then one-handed melee,
 * each by value (2026-10-10: "Sort weapons by 2 handed melee, ranged, and
 * then one handed melee"). Ranged is whatever trains ranged — a bow, a
 * crossbow, a rock — whatever hands it takes.
 *
 * Rings and cloaks each have a band of their own (2026-10-10: "sort cloaks
 * separately to armor"). Until then a ring sorted with the helmets, and a
 * cloak with the armor after every suit, since it is worn over one
 * (2026-10-04: "make the various elf items equippable in addition to other
 * items where it makes sense").
 *
 * Food has no order of its own (2026-09-25: "Don't worry about sorting
 * food, just make the auto eat eat the food that will free up a slot
 * soonest"). Which food goes down next is the server's to say — see
 * nextFood in internal/game/bagsort.go, which since 2026-10-08 eats the
 * heaviest food per second of eating first — and nothing about it is drawn
 * from where a food sits in the bag.
 *
 * That order is the one a bag starts in, and the bag's own dialog can swap
 * it for another (2026-10-08: "add sort by weight to the inventory context
 * menu"): by weight, the heaviest pile first — what one weighs times how
 * many there are, so a purse of coins weighs its count — and piles that
 * weigh the same in the order above. It is the picture's alone, like the
 * order it replaces: the server is not told, and the choice is kept in the
 * browser (see bagSort in ui.ts), so it lasts a reload but not a move to
 * another machine.
 *
 * Over both orders, the kinds the player has pinned go first (2026-10-17:
 * "allow item types to be pinned and unpinned to the top of the inventory.
 * the sorting is by pinned first, then within the pin/unpinned classes,
 * whatever the existing sort was"). A pin is on a kind, by item id, so
 * every pile of it is pinned, and the pinned piles are in the same order
 * among themselves as the rest are. Unlike the order, the pins are kept
 * with the character on the server (see internal/game/pins.go), so they
 * follow the character to another machine.
 */

/** How the bag is put in order: by kind, as above, or heaviest first. */
export type BagSort = "kind" | "weight";

/** A square of the bag: a kind, an owner, and everything there is of it. */
export type Square = Item;

/** The band the weapons are in, which is split by weaponOrder. */
const WEAPONS = 3;

/** Where a weapon goes among the weapons: two-handed melee, ranged, then
 *  one-handed melee. */
function weaponOrder(def: ItemDef): number {
  if (def.skill === "ranged") return 1;
  return def.twohanded ? 0 : 2;
}

/** Which band of the bag a square belongs in; see the order above. */
function groupOf(def: ItemDef | undefined, isGold: boolean): number {
  if (isGold) return 10;
  if (!def) return 8;
  if ((def.heal ?? 0) > 0 || (def.mana ?? 0) > 0) return 1;
  if (def.ring) return 2;
  if ((def.attack ?? 0) > 0) return WEAPONS;
  if ((def.shield ?? 0) > 0 || def.offhand) return 4;
  if ((def.armor ?? 0) > 0) return 5;
  if ((def.cloak ?? 0) > 0 || (def.belt ?? 0) > 0) return 6;
  if ((def.helm ?? 0) > 0) return 7;
  if ((def.food ?? 0) > 0) return 9;
  return 8;
}

export interface BagView {
  /** The squares, in the order they are drawn. */
  squares: Square[];
  /** Ids of what is in use, for outlining a square whatever it holds. */
  equipped: Set<number>;
  /** The piles in use, as `id|variant`, for telling which of two piles of
   *  one kind is the one on (see variant.go on the server). */
  equippedPiles: Set<string>;
  /** Ids picked by hand: a second click on one of these puts it away. */
  chosen: Set<number>;
  /** Ids of the kinds pinned to the top. */
  pinned: Set<number>;
  /** Something in use takes both hands, so a shield in the bag is down. */
  twoHanded: boolean;
}

/**
 * Lay a bag out for drawing.
 *
 * `equip` is what is in use, by id — the server decides that, since the
 * skills weigh in — and it is used to mark those squares, not to move them.
 *
 * The gold id is passed in rather than written down here for the same
 * reason the item table lives on the server: the numbers are the wire.
 */
export function layOutBag(
  inv: Item[],
  defs: Map<number, ItemDef>,
  opts: { equip: number[]; equipvars?: string[]; chosen?: number[]; pins?: number[]; goldID: number; sort?: BagSort },
): BagView {
  const equipped = new Set(opts.equip);
  const equippedPiles = new Set(opts.equip.map((id, i) => `${id}|${opts.equipvars?.[i] ?? ""}`));
  const chosen = new Set(opts.chosen ?? []);
  const pinned = new Set(opts.pins ?? []);
  const twoHanded = opts.equip.some((id) => defs.get(id)?.twohanded ?? false);

  // One square a pile, whatever is in it: see the note at the top.
  const squares: Square[] = inv.slice();

  // What is in use sorts with its kind rather than ahead of everything
  // (2026-09-27: "don't reorder equipped items to the start of the
  // inventory"); it is marked where it lies instead.
  const keyOf = (sq: Square) => {
    // A made piece weighs and is worth what its make makes it (2026-10-28),
    // and sorts by its kind's name among the rest of its kind.
    const kind = defs.get(sq.id);
    const def = sq.def ?? kind;
    const gold = sq.id === opts.goldID;
    const group = groupOf(def, gold);
    // What the pile weighs: a coin is the unit, so gold weighs its count
    // even from a table that does not say so.
    const cn = (def?.cn ?? (gold ? 1 : 0)) * sq.count;
    // What one of it is worth: the most valuable of a band goes first.
    const price = def?.price ?? (gold ? 1 : 0);
    return { group, sub: group === WEAPONS ? weaponOrder(def!) : 0, price, name: kind?.name ?? "", cn };
  };

  const keys = new Map<Square, ReturnType<typeof keyOf>>();
  for (const sq of squares) keys.set(sq, keyOf(sq));
  // Stable, so two squares that compare the same keep the order the piles
  // arrived in — which is the server's settled order, not a map's whim.
  squares.sort((a, b) => {
    const ka = keys.get(a)!, kb = keys.get(b)!;
    // Pinned kinds first, whichever order the rest is in.
    const pa = pinned.has(a.id), pb = pinned.has(b.id);
    if (pa !== pb) return pa ? -1 : 1;
    // By weight, the heaviest first, and the kind order between equals.
    if (opts.sort === "weight" && ka.cn !== kb.cn) return kb.cn - ka.cn;
    if (ka.group !== kb.group) return ka.group - kb.group;
    if (ka.sub !== kb.sub) return ka.sub - kb.sub;
    if (ka.price !== kb.price) return kb.price - ka.price;
    // By name whatever its capitals, so Sting sorts among the s's rather
    // than ahead of every weapon (2026-09-26, "Implement them all").
    const na = ka.name.toLowerCase(), nb = kb.name.toLowerCase();
    if (na !== nb) return na < nb ? -1 : 1;
    if (ka.name !== kb.name) return ka.name < kb.name ? -1 : 1;
    const oa = a.owner ?? "", ob = b.owner ?? "";
    if (oa !== ob) return oa < ob ? -1 : 1;
    const va = a.var ?? "", vb = b.var ?? "";
    if (va !== vb) return va.length !== vb.length ? va.length - vb.length : va < vb ? -1 : 1;
    return b.count - a.count;
  });

  return { squares, equipped, equippedPiles, chosen, pinned, twoHanded };
}
